import clsx from 'clsx'
import { useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import React, { useContext, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { ModalPanel, ModalSidebarContext, TabSwitch } from '../../../../../components/ModalSidebar'
import Groups from '../Groups'
import Licenses from '../Licenses'
import UserProperties from '../UserProperties'
import useHydrateAssignedLicenses from '../useHydrateAssignedLicenses'
import styles from './EditFormContent.module.scss'

function EditFormContent({
  modalSubmitStatusProp,
  submitErrorProp,
  employeeEmailOptionsProp,
  modalTabsProp,
  employeeProp,
  intlProp,
  isIntegratedProp = false,
  isGwsProp = false,
  allEmployeesGroupsProp,
  customerLicensesProp,
  isFetchingCustomerLicensesProp,
  errorFetchingCustomerLicensesProp,
  syncCustomerLicensesProp,
  isSyncingCustomerLicensesProp,
  assignedLicenseIdsProp,
  currentEmployeeIdProp,
  onLicensesAppliedProp,
}) {
  const { submitForm } = useFormikContext()
  const { onHideWithConfirmation, headerBackButtonAction } = useContext(ModalSidebarContext)
  useHydrateAssignedLicenses(assignedLicenseIdsProp, currentEmployeeIdProp, onLicensesAppliedProp)

  const errorBackButton = useMemo(
    () => ({
      variant: 'secondary',
      label: 'GENERAL.BACK',
      onClick: headerBackButtonAction || onHideWithConfirmation,
    }),
    [headerBackButtonAction, onHideWithConfirmation],
  )

  const handleFormSubmit = (e) => {
    e.preventDefault()
    submitForm()
  }

  return (
    <ModalPanel
      leftButton={modalSubmitStatusProp === 'error' ? errorBackButton : 'cancel'}
      rightButton={{
        label: 'GENERAL.SAVE',
        variant: 'primary',
        onClick: () => submitForm(),
        disabled: modalSubmitStatusProp === 'loading',
        id: 'edit-employee-data-submit-btn',
      }}
      tabs={modalTabsProp}
    >
      <form className={styles.form} onSubmit={handleFormSubmit} noValidate>
        {modalSubmitStatusProp === 'error' && (
          <div className={clsx('section px-4 py-3', styles.errorBanner)}>
            <div className="d-flex align-items-center">
              <i className={clsx('fas fa-circle-xmark me-3', styles.errorIcon)} />
              <div className="flex-grow-1">
                <h5 className="mb-1 text-color-danger">
                  <FormattedMessage id="GENERAL.ACTION_FAILED" />
                </h5>
                <p className={clsx('mb-0', styles.errorMessage)}>
                  {typeof submitErrorProp === 'string' && submitErrorProp.startsWith('GENERAL.') ? (
                    <FormattedMessage id={submitErrorProp} />
                  ) : (
                    submitErrorProp
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        <TabSwitch tab="userproperties">
          <UserProperties
            employee={employeeProp}
            intl={intlProp}
            employeeEmailOptions={employeeEmailOptionsProp}
            isIntegrated={isIntegratedProp}
          />
        </TabSwitch>

        {isIntegratedProp && !isGwsProp && (
          <TabSwitch tab="groups">
            <Groups
              employee={employeeProp}
              intl={intlProp}
              allEmployeesGroups={allEmployeesGroupsProp}
              isGws={isGwsProp}
            />
          </TabSwitch>
        )}

        {isIntegratedProp && (
          <TabSwitch tab="licenses">
            <Licenses
              employee={employeeProp}
              intl={intlProp}
              customerLicenses={customerLicensesProp}
              isFetchingCustomerLicenses={isFetchingCustomerLicensesProp}
              errorFetchingCustomerLicenses={errorFetchingCustomerLicensesProp}
              syncCustomerLicenses={syncCustomerLicensesProp}
              isSyncingCustomerLicenses={isSyncingCustomerLicensesProp}
              isGws={isGwsProp}
            />
          </TabSwitch>
        )}
      </form>
    </ModalPanel>
  )
}

EditFormContent.propTypes = {
  modalSubmitStatusProp: PropTypes.string,
  submitErrorProp: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  employeeEmailOptionsProp: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number,
    }),
  ),
  modalTabsProp: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.node,
    }),
  ),
  employeeProp: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  intlProp: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  isIntegratedProp: PropTypes.bool,
  isGwsProp: PropTypes.bool,
  allEmployeesGroupsProp: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  customerLicensesProp: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  isFetchingCustomerLicensesProp: PropTypes.bool,
  errorFetchingCustomerLicensesProp: PropTypes.bool,
  syncCustomerLicensesProp: PropTypes.func,
  isSyncingCustomerLicensesProp: PropTypes.bool,
  assignedLicenseIdsProp: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  currentEmployeeIdProp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onLicensesAppliedProp: PropTypes.func,
}

export default React.memo(EditFormContent)
